import { CommonModule, DatePipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { PatientsModule } from 'src/app/patients/patients.module';
import { TreatmentPickerModule } from 'src/app/treatment-picker/treatment-picker.module';
import { TextFavoriteModule } from '../../logged-in-navbar/clinic-setup-modal/text-favorite/text-favorite.module';
import { AttachmentsModule } from '../attachments/attachments.module';
import { UpdateOnBlurModule } from '../directives/update-on-blur/update-on-blur.module';
import { InfoBoxModule } from '../info-box/info-box.module';
import { InfoPopoverModule } from '../info-popover/info-popover.module';
import { NiceDateInputModule } from '../nice-date-input/nice-date-input.module';
import { SharedPipesModule } from '../shared-pipes/shared-pipes.module';
import { SharedModule } from '../shared.module';
import { SymptomsPipesModule } from '../symptoms/pipes/symptoms-pipes.module';
import { SymptomsModule } from '../symptoms/symptoms.module';
import { InterpretationModule } from './../interpretation/interpretation.module';
import { PatientFormModule } from './../patient-form/patient-form.module';
import { AssessmentAiScribeModule } from './assessment-ai-scribe/assessment-ai-scribe.module';
import { AssessmentFormComponent } from './assessment-form/assessment-form.component';
import { ClinicFormComponent } from './clinic-form/clinic-form.component';
import { ConsultFormsErrorStateMatcher } from './consult-forms-error-state-matcher';
import { DoctorAndClinicInfoWizardModalComponent } from './doctor-and-clinic-info-wizard-modal/doctor-and-clinic-info-wizard-modal.component';
import { DoctorFormModule } from './doctor-form/doctor-form.module';
import { AttachTemplateModalComponent } from './dry-eye-forms/attach-template-modal/attach-template-modal.component';
import { DoctorNotesCardComponent } from './dry-eye-forms/dry-eye-assessment-form/doctor-notes-card.component';
import { DryEyeAssessmentFormComponent } from './dry-eye-forms/dry-eye-assessment-form/dry-eye-assessment-form.component';
import { DryEyeCategoryComponent } from './dry-eye-forms/dry-eye-category/dry-eye-category.component';
import { DryEyeAssessmentMethodFormComponent } from './dry-eye-forms/dry-eye-form/dry-eye-assessment-method-form/dry-eye-assessment-method-form.component';
import { DryEyeFormComponent } from './dry-eye-forms/dry-eye-form/dry-eye-form.component';
import { DryEyeFormsComponent } from './dry-eye-forms/dry-eye-forms.component';
import { DryEyeMedicalHistoryFormComponent } from './dry-eye-forms/dry-eye-medical-history-form/dry-eye-medical-history-form.component';
import { MedicalHistoryAiSectionComponent } from './dry-eye-forms/dry-eye-medical-history-form/medical-history-ai-section/medical-history-ai-section.component';
import { MedicalHistoryFieldsComponent } from './dry-eye-forms/dry-eye-medical-history-form/medical-history-fields/medical-history-fields.component';
import { MedicalHistoryQuickScanComponent } from './dry-eye-forms/dry-eye-medical-history-form/medical-history-quick-scan/medical-history-quick-scan.component';
import { PreviousHistoryDropdownComponent } from './dry-eye-forms/dry-eye-medical-history-form/previous-history-dropdown/previous-history-dropdown.component';
import { SymptomDurationComponent } from './dry-eye-forms/dry-eye-medical-history-form/symptom-duration/symptom-duration.component';
import { ToggleableMedicalHistoryTextFieldComponent } from './dry-eye-forms/dry-eye-medical-history-form/toggleable-medical-history-text-field/toggleable-medical-history-text-field.component';
import { DryEyeSymptomFormComponent } from './dry-eye-forms/dry-eye-symptom-form/dry-eye-symptom-form.component';
import { PreOpAnalysisComponent } from './dry-eye-forms/pre-op/pre-op-analysis/pre-op-analysis.component';
import { PreOpReportBranchComponent } from './dry-eye-forms/pre-op/pre-op-report/pre-op-report-branch/pre-op-report-branch.component';
import { PreOpReportComponent } from './dry-eye-forms/pre-op/pre-op-report/pre-op-report.component';
import { PreOpSignsComponent } from './dry-eye-forms/pre-op/pre-op-signs/pre-op-signs.component';
import { PreOpComponent } from './dry-eye-forms/pre-op/pre-op.component';
import { WeatherComponent } from './dry-eye-forms/weather/weather.component';
import { EmrIntegrationFormComponent } from './emr-integration-form/emr-integration-form.component';
import { MedicalHistoryFormComponent } from './medical-history-form/medical-history-form.component';
import { PatientModalComponent } from './patient-modal/patient-modal.component';
import { RealtimeMediaModalComponent } from './realtime-media-modal/realtime-media-modal.component';
import { DewsIIIDiagnosisComponent } from './dry-eye-forms/dews-iii/dews-iii-diagnosis/dews-iii-diagnosis.component';
import { DewsIIIDriversComponent } from './dry-eye-forms/dews-iii/dews-iii-drivers/dews-iii-drivers.component';
import { DewsIIIHomeostasisMarkersComponent } from './dry-eye-forms/dews-iii/dews-iii-homeostasis-markers/dews-iii-homeostasis-markers.component';
import { DewsIIIMedicalHistoryComponent } from './dry-eye-forms/dews-iii/dews-iii-medical-history/dews-iii-medical-history.component';
import { DewsIIISummaryComponent } from './dry-eye-forms/dews-iii/dews-iii-summary/dews-iii-summary.component';
import { DewsIIIImpressionsRecommendationsComponent } from './dry-eye-forms/dews-iii/dews-iii-impressions-recommendations/dews-iii-impressions-recommendations.component';
import { DewsIIIAnalyticsPanelComponent } from './dry-eye-forms/dews-iii/dews-iii-analytics-panel/dews-iii-analytics-panel.component';
import { Osdi6LinkSuggestionComponent } from './dry-eye-forms/dews-iii/osdi6-link-suggestion/osdi6-link-suggestion.component';
import { DewsIIISignsComponent } from './dry-eye-forms/dews-iii/dews-iii-signs/dews-iii-signs.component';
import { DewsIIIService } from './dry-eye-forms/dews-iii/dews-iii.service';
import { NutritionBasedTestingComponent } from './dry-eye-forms/nutrition-based-testing/nutrition-based-testing.component';
import { RecommendationsModule } from './recommendations/recommendations.module';
import { SymptomValidatorsService } from './validators/symptom-validators/symptom-validators.service';
@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    UpdateOnBlurModule,
    NiceDateInputModule,
    AttachmentsModule,
    SymptomsModule,
    SymptomsPipesModule,
    InfoBoxModule,
    InfoPopoverModule,
    SharedPipesModule,
    TreatmentPickerModule,
    SharedModule,
    InterpretationModule,
    DoctorFormModule,
    MatExpansionModule,
    TextFavoriteModule,
    PatientsModule,
    PatientFormModule,
    AssessmentAiScribeModule,
    RecommendationsModule
  ],
  declarations: [
    PatientModalComponent,
    MedicalHistoryFormComponent,
    AssessmentFormComponent,
    DryEyeFormComponent,
    DryEyeAssessmentFormComponent,
    DryEyeMedicalHistoryFormComponent,
    DryEyeAssessmentMethodFormComponent,
    DryEyeFormsComponent,
    DryEyeCategoryComponent,
    ClinicFormComponent,
    EmrIntegrationFormComponent,
    DoctorAndClinicInfoWizardModalComponent,
    PreviousHistoryDropdownComponent,
    ToggleableMedicalHistoryTextFieldComponent,
    WeatherComponent,
    SymptomDurationComponent,
    PreOpComponent,
    DryEyeSymptomFormComponent,
    MedicalHistoryFieldsComponent,
    MedicalHistoryQuickScanComponent,
    MedicalHistoryAiSectionComponent,
    PreOpSignsComponent,
    PreOpAnalysisComponent,
    PreOpReportComponent,
    PreOpReportBranchComponent,
    RealtimeMediaModalComponent,
    DewsIIIMedicalHistoryComponent,
    DewsIIIHomeostasisMarkersComponent,
    DewsIIIDiagnosisComponent,
    DewsIIIDriversComponent,
    DewsIIISummaryComponent,
    DewsIIIImpressionsRecommendationsComponent,
    DewsIIIAnalyticsPanelComponent,
    Osdi6LinkSuggestionComponent,
    DewsIIISignsComponent,
    NutritionBasedTestingComponent,
    AttachTemplateModalComponent,
    DoctorNotesCardComponent
  ],
  exports: [
    PatientModalComponent,
    MedicalHistoryFormComponent,
    AssessmentFormComponent,
    DryEyeFormsComponent,
    DryEyeCategoryComponent,
    ClinicFormComponent,
    EmrIntegrationFormComponent,
    DoctorAndClinicInfoWizardModalComponent,
    DoctorFormModule,
    DryEyeMedicalHistoryFormComponent,
    DryEyeAssessmentMethodFormComponent,
    DryEyeFormComponent,
    DryEyeAssessmentFormComponent,
    MedicalHistoryQuickScanComponent,
    MedicalHistoryAiSectionComponent,
    WeatherComponent,
    PreOpComponent,
    DewsIIIMedicalHistoryComponent,
    DewsIIIHomeostasisMarkersComponent,
    DewsIIIDiagnosisComponent,
    DewsIIIDriversComponent,
    DewsIIISummaryComponent,
    DewsIIIImpressionsRecommendationsComponent,
    DewsIIIAnalyticsPanelComponent,
    Osdi6LinkSuggestionComponent,
    DewsIIISignsComponent,
    NutritionBasedTestingComponent
  ],
  providers: [
    DatePipe,
    SymptomValidatorsService,
    DewsIIIService,
    { provide: ErrorStateMatcher, useClass: ConsultFormsErrorStateMatcher }
  ],
  entryComponents: [PreOpReportComponent, RealtimeMediaModalComponent, AttachTemplateModalComponent]
})
export class ConsultFormsModule {}
